// ---------------------------------------------------------------------------
// Screen-design read-back — the deterministic half of S4 in
// docs/studio/wonbanking-studio-scenarios-2026-08-08.md.
//
// A design document carries the layout, the label copy, and the screen flow —
// and usually NOT where the data comes from. What this module reads is
// exactly what the scenarios doc says is decidable: the area composition
// (heading lines), the labels the document QUOTES (carried verbatim — that
// is the doc's explicit rule), and the flow lines (arrows). What it does not
// even attempt is data bindings: S4's UI implication is that the surface
// must SAY "the screen is here, the data is not" and point at the S1
// contract / S2 field-table lanes, rather than let generation guess.
//
// Proceeding screen-only is allowed but explicit: the serialized decision
// records that the human was told and chose, so downstream never mistakes
// absence for oversight.
//
// The gate is deliberately strict about prose. Dash bullets are the common
// prose list shape and do not count as areas; a paste qualifies only with
// real document structure (markdown/numbered/■-style headings or lines
// naming an 영역) AND quoted labels or flow arrows, across enough lines.
// JSON, field tables, and markup are refused here so the parser stays safe
// under router reordering.
// ---------------------------------------------------------------------------

export interface ScreenDesignUnread {
  /**
   * Headings this read RECOGNIZED as document structure and then reported
   * nowhere: not as an area (they name a flow/process SECTION, not a region
   * of the screen), not as the title, and — because their section holds no
   * arrow line — not through `flows` either.
   *
   * Without this list the card silently shrinks the document: "2 areas" for
   * a three-section paste, with the third section's own words nowhere on
   * screen. Every other intake parser in this lane discloses what it could
   * not show; a filter added without one is the same defect wearing the
   * opposite sign.
   *
   * A flow section whose lines DO reach the card's flow list is NOT here —
   * that content is already visible, and a notice about it would be noise.
   */
  headings: string[]
  /**
   * Field-code-SHAPED tokens the read saw and could not confirm as data
   * references, because nothing in the document says what they are bound to.
   *
   * They are not declarations — counting them is how a screen id became one.
   * They are not nothing either: without this list the human is asked to
   * confirm "no data source is declared" over a document that visibly holds
   * `CUST_NO`, and the planner is then told a human ruled on an absence
   * nobody was shown. An English document whose only data vocabulary is
   * "maps to", and a Korean mapping list under a heading that does not itself
   * bind, both land here.
   *
   * Tokens the read is CONFIDENT are not field codes — screen ids, AS_IS/TO_BE
   * — are absent by design. A channel that fired on every document would be
   * noise, and noise is the failure mode of a disclosure channel.
   */
  dataCandidates: string[]
}

export interface ScreenDesignReadback {
  source: 'screen-design'
  /**
   * The document's own title, when its first line is a heading that names the
   * document rather than a screen area. Kept out of `areas`: a read-back that
   * counts the title as a screen area asks the human to confirm a screen the
   * document does not describe.
   */
  title: string | null
  /** Heading lines that name a screen AREA, verbatim after marker stripping. */
  areas: string[]
  /** Quoted strings, verbatim, first-seen order, duplicates collapsed. */
  labels: string[]
  /** Lines containing a flow arrow, verbatim after bullet markers. */
  flows: string[]
  /** Structure the read recognized and the card cannot show. */
  unread: ScreenDesignUnread
  /**
   * Machine-readable data references the document declares — HTTP endpoints,
   * `전문` transaction ids, UPPER_SNAKE field codes — first-seen order,
   * deduplicated. Empty means the read found none, which is the ONLY thing
   * that may be reported as "no data source is declared".
   */
  dataDeclarations: string[]
}

// "…", '…', “…”, 『…』, 「…」 — 1..30 chars, single line.
const QUOTED_LABEL_PATTERN = /["'“『「]([^"'“”』」\n]{1,30})["'”』」]/g
const FLOW_ARROW_PATTERN = /→|->/
const HTML_CONTROL_PATTERN = /<(?:form|table|input|select|textarea|button)\b/gi

/** Markdown #, numbered "1.", or ■□▶●○ document bullets — NOT dash bullets. */
const HEADING_MARKER_PATTERN = /^(?:#{1,4}\s+|\d{1,2}[.)]\s+|[■□▶●○◎]\s*)/
const AREA_WORD_PATTERN = /(?:영역|화면|구성)\s*$/
/**
 * A heading that names the document's flow SECTION, whose contents the card
 * already renders under "screen flow". It is a section of the document, not a
 * region of the screen. `AREA_WORD_PATTERN` wins over this: "주문 흐름 영역"
 * says 영역 and is an area.
 */
const FLOW_SECTION_PATTERN = /(?:흐름|플로우|flow|프로세스|process)/i

// ── Data-source declarations (S4's one claim about what is NOT in the doc) ──
/** `GET /api/loan/limit/list` — a method and a path, written out. */
const HTTP_ENDPOINT_PATTERN = /\b(GET|POST|PUT|PATCH|DELETE)\s+(\/[A-Za-z0-9_\-./{}]+)/g
/** `전문 LON0004I` / `응답 전문 LON0004O` — a named transaction id. */
const TRANSACTION_ID_PATTERN = /전문\s+([A-Z][A-Z0-9]{3,})\b/g
/**
 * `CUST_NO`, `LMT_AMT` — the UPPER_SNAKE field code of a message spec.
 *
 * NOT high-precision on its own, unlike the two patterns above: it matches
 * EVERY Latin UPPER_SNAKE token, and these documents are full of tokens that
 * are not field codes — the screen id in the header (`MWLON004_01`, this
 * corpus's own id shape) and the AS_IS/TO_BE prose markers being the two
 * measured ones. Read unconditionally, it flipped `dataSource` to
 * declared-in-document over a document that declares nothing, which deleted
 * the gap notice S4 exists to render and told the planner to bind to
 * `MWLON004_01` and invent nothing else. So a match here is a CANDIDATE; it
 * counts only with the binding evidence below.
 */
const FIELD_CODE_PATTERN = /\b([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+)\b/g

/**
 * The document SAYING that something MOVES BETWEEN a screen element and a
 * message or API. Every entry has to denote that movement or correspondence;
 * a word that merely occurs near data does not qualify.
 *
 * Dropped, and why — the same objection each time:
 *   `데이터` / `연동`  ordinary header vocabulary ("(데이터 연동 포함)"), and a
 *                     header is exactly where the screen id lives;
 *   `응답` / `요청`    ordinary UI-behaviour vocabulary in this genre
 *                     ("응답이 늦으면 스피너를 표시합니다"), which is how a
 *                     screen id came back as a declared data reference;
 *   `세팅`            generic UI verb ("기본값 세팅").
 *
 * Not admitted despite being asked for: `항목` / `필드` / `정의` / `코드`.
 * "입력 필드는 필수입니다" is on nearly every one of these documents, so
 * admitting them would re-open the whole C1 class. A mapping list headed
 * `화면 항목 정의` therefore declares nothing — and its codes come back
 * through `unread.dataCandidates` instead of being dropped.
 *
 * Korean only, knowingly. An English document writing "maps to CUST_NO"
 * declares nothing here; its codes are disclosed as candidates so the human
 * confirming "no data source" has actually been shown what was found.
 */
const DATA_BINDING_WORD_PATTERN = /매핑|바인딩|전송|수신|전문/

/**
 * Tokens of this feature's own vocabulary — an AS-IS/TO-BE conversion note is
 * about the document, never a field of a message spec. Listed because they
 * are the one class that survives the binding gate honestly (a line may well
 * say "AS_IS 화면의 항목을 그대로 전송합니다"), not because the gate is weak.
 */
const NON_FIELD_CODE_TOKENS = new Set(['AS_IS', 'TO_BE'])

/**
 * The ONE shape this read is confident about: a screen id.
 * `MWACC001_02`, `MWLON004_01` — a first segment of letters-then-digits (the
 * system+module+number of a screen), ending on a purely numeric segment.
 *
 * Confidence is what buys silence, and silence is what keeps the candidate
 * channel usable: every one of these documents carries a screen id in its
 * header, so a channel that listed them would fire on all of them.
 *
 * Deliberately narrower than "ends on a numeric segment", which is an
 * ordinary field-code convention: `ADDR_1`, `HP_NO_1`, `TEL_NO_14`, `PWNO_6`,
 * `ACTNO_15` are fields, not screens, and their first segment is plain
 * letters. `ADDR1_NM` has the letters-then-digits first segment but does not
 * end numerically, so it is a field too.
 */
function looksLikeScreenId(code: string): boolean {
  const segments = code.split('_')
  if (segments.length < 2) return false
  return (
    /^[A-Z]+\d+$/.test(segments[0]) &&
    /^\d+$/.test(segments[segments.length - 1])
  )
}

/**
 * Whether a token is PLAUSIBLY a message-spec field code — at least one
 * segment carrying a word. `N_A` ("N/A" in caps) has none; `E_MAIL`,
 * `H_RETURN_CD`, `FROM_D`, `MAP_X_PT`, `SVC_M_ID` all do.
 *
 * This decides COUNTING only. A token that fails it is NOT silent: it goes to
 * `unread.dataCandidates`, because "this does not look like a field to me" is
 * not the same claim as "this is not a field", and only the first is
 * something this read can support.
 *
 * The earlier version of this function required EVERY segment to be two or
 * more characters and refused any numeric final segment, and it ran before
 * the bound check — so what it refused reached neither list. Measured across
 * the corpus (29,993 distinct UPPER_SNAKE tokens in 13,091 source files) that
 * silently dropped 19.0% of the vocabulary, of which only 11.6% was the
 * screen-id shape it was written for. `S_PSBZ_NO`, `HP_NO_1`, `H_RETURN_CD`,
 * `ACT_PWNO_10` and `SVC_M_ID` are among the most frequent casualties.
 */
function looksLikeFieldCode(code: string): boolean {
  return code.split('_').some((segment) => segment.length >= 2)
}

/** `전문 POST /api/…` — `POST` is the verb of the endpoint, not a 전문 id. */
const HTTP_METHOD_TOKENS = new Set([
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'HEAD',
  'OPTIONS',
])

/**
 * `- …` / `* …` / `• …` — content, whatever word it happens to end on.
 *
 * A document marks its sections with `#`, `1.` or `■`; a dash is a list item.
 * So a bullet neither OPENS nor CLOSES a section, and both halves of that are
 * deliberate:
 *
 *   - it does not close one, so `- 참고 영역` inside a `## 데이터 매핑` section
 *     leaves the section binding and the codes after it are still counted.
 *     Spelled `## 참고 영역` the section really does end, and those codes are
 *     disclosed as candidates instead. That asymmetry is the intended trade:
 *     the alternative is the truncation defect, where a prose bullet silently
 *     closed a mapping list and its remaining codes disappeared from a list
 *     the planner is told is exhaustive. Over-counting inside a section the
 *     document ITSELF headed "데이터 매핑" is the cheaper error, and the
 *     document's own heading syntax is what decides where that section ends.
 *   - it does not open one, so a bullet is never shown to the human as a
 *     screen area.
 */
const BULLET_LINE_PATTERN = /^[-*•]\s/

function stripHeadingMarkers(line: string): string {
  let value = line.trim()
  // "## 1. 조회조건 영역" carries two markers; strip until none remain.
  for (;;) {
    const next = value.replace(HEADING_MARKER_PATTERN, '')
    if (next === value) return value.trim()
    value = next.trim()
  }
}

function stripBulletMarkers(line: string): string {
  return line.trim().replace(/^[-■□▶●○◎*]\s*/, '')
}

export function parseScreenDesignReadback(
  text: string,
): ScreenDesignReadback | null {
  const trimmed = text.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('```')) return null

  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
  if (lines.length < 5) return null

  // Other intake lanes own these shapes.
  if (lines.filter((line) => line.includes('\t')).length >= 2) return null
  if (
    trimmed.includes('<%') ||
    (trimmed.match(HTML_CONTROL_PATTERN) ?? []).length >= 3
  ) {
    return null
  }

  const headings: Array<{ name: string; index: number }> = []
  const flows: string[] = []
  const flowIndices: number[] = []
  lines.forEach((line, index) => {
    // A bullet is CONTENT. `AREA_WORD_PATTERN` matches any line ending in
    // 영역/화면/구성, so without this a prose bullet ("- 이하 항목은 상세 화면")
    // was read as a heading — it appeared on the card as a screen area, and it
    // silently closed the enclosing section for the data read, dropping every
    // field code after it out of a list the planner is told is exhaustive.
    // `■`-style document bullets stay headings; `-`, `*` and `•` do not (see
    // BULLET_LINE_PATTERN, which also records what that costs).
    const isHeading =
      !BULLET_LINE_PATTERN.test(line) &&
      (HEADING_MARKER_PATTERN.test(line) || AREA_WORD_PATTERN.test(line))
    // A heading is structure, not content: short, and not itself a flow line.
    if (isHeading && !FLOW_ARROW_PATTERN.test(line)) {
      const name = stripHeadingMarkers(line)
      if (name && name.length <= 60) {
        headings.push({ name, index })
        return
      }
    }
    if (FLOW_ARROW_PATTERN.test(line)) {
      flows.push(stripBulletMarkers(line))
      flowIndices.push(index)
    }
  })

  const labels: string[] = []
  for (const match of trimmed.matchAll(QUOTED_LABEL_PATTERN)) {
    const label = match[1].trim()
    if (label && !labels.includes(label)) labels.push(label)
  }

  // Structure alone is not a design doc, and quotes alone are prose: the
  // combination is what a pasted 설계서 reliably has. The gate counts
  // HEADINGS, not areas: which headings turn out to be areas is a question
  // about what the card may claim, and must not narrow what the lane accepts.
  if (headings.length < 2) return null
  if (labels.length < 2 && flows.length === 0) return null

  // The first content line, when it is a heading that does not name an area,
  // is the document naming ITSELF. Only taken as a title when headings remain
  // for the areas — a two-heading document has no title to spare.
  const first = headings[0]
  const isTitle =
    first.index === 0 &&
    !AREA_WORD_PATTERN.test(first.name) &&
    headings.length >= 3
  const title = isTitle ? first.name : null

  const body = headings.filter((heading) => !(isTitle && heading === first))
  const isArea = (name: string): boolean =>
    AREA_WORD_PATTERN.test(name) || !FLOW_SECTION_PATTERN.test(name)
  const areas = body
    .filter((heading) => isArea(heading.name))
    .map((heading) => heading.name)

  // What the filter above removed, minus what the card shows anyway. A
  // section "owns" the lines between its heading and the next heading; if any
  // of them is a flow line, the human sees that section's content under the
  // card's flow list and needs no notice about it.
  const unreadHeadings = body
    .filter((heading) => !isArea(heading.name))
    .filter((heading) => {
      const next = headings.find((other) => other.index > heading.index)
      const end = next ? next.index : Number.POSITIVE_INFINITY
      return !flowIndices.some(
        (flowIndex) => flowIndex > heading.index && flowIndex < end,
      )
    })
    .map((heading) => heading.name)

  const data = readDataDeclarations(
    trimmed,
    lines,
    new Set(headings.map((heading) => heading.index)),
    isTitle ? first.index : null,
  )

  return {
    source: 'screen-design',
    title,
    areas,
    labels,
    flows,
    unread: { headings: unreadHeadings, dataCandidates: data.uncounted },
    dataDeclarations: data.declarations,
  }
}

/**
 * What the document says about where its data comes from. S4's card exists to
 * say "the screen is here, the data is not" — a claim that is only honest
 * after a read that could have found data. Every pattern here is a literal
 * machine-readable reference, so what comes back can be shown to the human
 * verbatim and checked against their document.
 */
function readDataDeclarations(
  text: string,
  lines: string[],
  headingIndices: ReadonlySet<number>,
  titleIndex: number | null,
): { declarations: string[]; uncounted: string[] } {
  const found: string[] = []
  const add = (value: string): void => {
    const entry = value.trim()
    if (entry && !found.includes(entry)) found.push(entry)
  }
  for (const match of text.matchAll(HTTP_ENDPOINT_PATTERN)) {
    add(`${match[1].toUpperCase()} ${match[2]}`)
  }
  for (const match of text.matchAll(TRANSACTION_ID_PATTERN)) {
    // `전문 POST /api/acct/list 로 전송합니다` — the token after 전문 is the
    // endpoint's verb, and listing it as a transaction id put `POST` in front
    // of the human and the planner as a data reference of its own.
    if (!HTTP_METHOD_TOKENS.has(match[1])) add(match[1])
  }

  // Field codes, only where the document binds them. Evidence counts on the
  // code's OWN line, or on the heading of the section it sits under — a
  // mapping section lists one code per line under a "데이터 매핑" heading, and
  // a line-only gate would read that whole section as declaring nothing.
  //
  // The headings are the ones the PARSE found, not ones re-derived here. Two
  // defects came from re-deriving them: a prose bullet ending in 화면 closed
  // the enclosing section mid-list, and the document TITLE opened a section of
  // its own — so "(화면ID: MWLON004_01, 전문 연동 포함)" bound its own screen id.
  // The title is excluded outright: it names the document, and a binding word
  // in it says the DOCUMENT concerns data, not that any particular token is a
  // field.
  const uncounted: string[] = []
  let sectionBinds = false
  lines.forEach((line, index) => {
    if (headingIndices.has(index)) {
      sectionBinds = index === titleIndex ? false : hasBindingEvidence(line)
    }
    const bound = sectionBinds || hasBindingEvidence(line)
    for (const match of line.matchAll(FIELD_CODE_PATTERN)) {
      const code = match[1]
      // SILENT — and only here. The read is confident this is not a field
      // code: this feature's own AS_IS/TO_BE vocabulary, or a screen id,
      // which every one of these documents carries in its header. Anything
      // else that fell in here would be a third category, neither counted nor
      // disclosed, which is what this lane exists to prevent.
      if (NON_FIELD_CODE_TOKENS.has(code) || looksLikeScreenId(code)) continue
      // COUNTED — the document says what it is bound to, and it is plausibly
      // a field.
      if (bound && looksLikeFieldCode(code)) {
        add(code)
        continue
      }
      // DISCLOSED — everything else. Either the document never said what the
      // code is bound to, or it does not look like a field to this reader.
      // Both are things the read is UNSURE of, and neither is something it may
      // drop: the human is about to rule on this document's data source and
      // can only rule on what they were shown, and the planner is told the
      // reference list is complete only when nothing landed here.
      if (!uncounted.includes(code) && !found.includes(code)) {
        uncounted.push(code)
      }
    }
  })
  return {
    declarations: found,
    uncounted: uncounted.filter((code) => !found.includes(code)),
  }
}

/**
 * The document saying that the thing just named does NOT exist.
 *
 * `## 데이터 매핑 없음` is the clearest signal one of these documents can give,
 * and testing for the PRESENCE of a relation word read it as the opposite: the
 * heading enabled the section and every code under it was serialized as a
 * declared data reference, telling the planner the document declares bindings
 * while the document says it does not.
 *
 * WHAT A NEGATED SECTION DOES TO ITS CODES: they are DISCLOSED as candidates,
 * not silenced. The silent path is a claim about a TOKEN — "this is not a
 * field code" (AS_IS/TO_BE, a screen id). A negation is a claim about the
 * SECTION's bindings and says nothing about whether `CUST_NO` is a field name,
 * so the silent path's justification does not transfer. Two consequences
 * decide it:
 *   - a NEUTRAL heading (`## 화면 항목 정의`) already discloses its codes, so
 *     silencing an EXPLICIT "no mapping" heading would make a stronger
 *     non-binding signal yield strictly less information than a weaker one;
 *   - silence would put the human in front of the gap notice with no mention
 *     of codes their document visibly contains — the exact defect the
 *     candidate channel was added for.
 * So a negated section is simply a NON-binding section. No new path.
 *
 * COVERED: 없음 · 없습니다 · 없다 · 없으며 · 없으나 · 없는 · 미정 · 미사용 ·
 * 미적용 · 불필요 · 제외 · N/A.
 *
 * NOT COVERED, and not implied to be:
 *   - English negation ("no mapping", "not applicable", "TBD") — the same
 *     Korean-only boundary already disclosed on DATA_BINDING_WORD_PATTERN;
 *   - a negation on a DIFFERENT line from the relation word ("2. 데이터 매핑"
 *     followed by "위 항목은 해당 없음");
 *   - sentence-level negation without one of these markers ("매핑은 아직
 *     정해지지 않았다").
 * Each of those still over-reads, and lands in `dataDeclarations`.
 */
const NEGATION_PATTERN =
  /없음|없습니다|없다|없으[며나]|없는|미정|미사용|미적용|불필요|제외|N\/A/

/**
 * A clause boundary. The negation only counts against the binding when it is
 * in the SAME clause as the relation word: "…에 매핑합니다. 기본값 없음." is an
 * affirmative binding with an unrelated trailing note, and a whole-line rule
 * would have demoted it to a candidate. `:` is deliberately NOT here — it is a
 * label separator, and "데이터 매핑: 없음" is a negation.
 */
const CLAUSE_BREAK_PATTERN = /[.,;()[\]{}]/

/** End offset of the LAST binding evidence on the line, or -1 if none. */
function lastBindingEvidenceEnd(line: string): number {
  let end = -1
  const relation = new RegExp(DATA_BINDING_WORD_PATTERN.source, 'g')
  for (const match of line.matchAll(relation)) {
    end = Math.max(end, match.index + match[0].length)
  }
  for (const match of line.matchAll(HTTP_ENDPOINT_PATTERN)) {
    end = Math.max(end, match.index + match[0].length)
  }
  for (const match of line.matchAll(TRANSACTION_ID_PATTERN)) {
    end = Math.max(end, match.index + match[0].length)
  }
  return end
}

/**
 * Whether this ONE line says that something is bound to data — a relation
 * word, an endpoint written out, or a named transaction, NOT followed by a
 * negation of it. The negation is resolved here, BEFORE a heading can enable
 * its section, rather than subtracted from the declarations afterwards: a
 * section that was never enabled has nothing to subtract.
 */
function hasBindingEvidence(line: string): boolean {
  const end = lastBindingEvidenceEnd(line)
  if (end === -1) return false
  const rest = line.slice(end)
  const negation = NEGATION_PATTERN.exec(rest)
  if (!negation) return true
  // A clause boundary between the two means the negation is about something
  // else, and the binding stands.
  return CLAUSE_BREAK_PATTERN.test(rest.slice(0, negation.index))
}

/**
 * The record of what the human was told and chose. The verbatim labels ride
 * along because the scenarios doc's rule is explicit: what the document
 * quotes is used as written, not re-invented downstream.
 *
 * `data-source` has two truths, and which one is written is decided by the
 * READ, not by the lane. Writing "undeclared — proceed screen-first" over a
 * document that names its endpoints and field codes made the generator
 * discard those bindings ("NO invented API endpoints or field bindings") and
 * claim a human had confirmed their absence.
 */
export function formatScreenDesignDecision(
  readback: ScreenDesignReadback,
): string {
  const lines = ['[screen-design-decision]']
  if (readback.labels.length > 0) {
    lines.push(`labels-verbatim: ${readback.labels.join(', ')}`)
  }
  if (readback.dataDeclarations.length > 0) {
    lines.push('data-source: declared — read from the document')
    lines.push(
      `data-source-declared: ${readback.dataDeclarations.join(', ')}`,
    )
  } else {
    lines.push('data-source: undeclared — proceed screen-first')
  }
  // Code-shaped tokens the read could not confirm. Recorded on BOTH branches:
  // beside a screen-first decision they are what the human was shown before
  // confirming an absence, and beside a declared one they are the reason the
  // reference list must not be quoted downstream as exhaustive.
  if (readback.unread.dataCandidates.length > 0) {
    lines.push(
      `data-source-uncounted: ${readback.unread.dataCandidates.join(', ')}`,
    )
  }
  return lines.join('\n')
}
